import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { restaurantInfo } from '../data/mock';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success('Messaggio inviato con successo! Ti risponderemo presto.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contatti" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-4">
            Contatti
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Vieni a Trovarci
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Siamo qui per te. Contattaci per informazioni o prenotazioni.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                Informazioni di Contatto
              </h3>
              <div className="space-y-6">
                {/* Address */}
                <Card className="p-6 border-slate-200 hover:border-amber-400 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Indirizzo</h4>
                      <p className="text-slate-600 text-sm">
                        {restaurantInfo.address.street}<br />
                        {restaurantInfo.address.zip} {restaurantInfo.address.city}<br />
                        {restaurantInfo.address.province}, {restaurantInfo.address.country}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Phone */}
                <Card className="p-6 border-slate-200 hover:border-amber-400 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Telefono</h4>
                      <a
                        href={`tel:${restaurantInfo.phone}`}
                        className="text-amber-600 hover:text-amber-700 transition-colors"
                      >
                        {restaurantInfo.phone}
                      </a>
                    </div>
                  </div>
                </Card>

                {/* Email */}
                <Card className="p-6 border-slate-200 hover:border-amber-400 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                      <a
                        href={`mailto:${restaurantInfo.email}`}
                        className="text-amber-600 hover:text-amber-700 transition-colors"
                      >
                        {restaurantInfo.email}
                      </a>
                    </div>
                  </div>
                </Card>

                {/* Hours */}
                <Card className="p-6 border-slate-200 hover:border-amber-400 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 mb-2">Orari di Apertura</h4>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p><span className="font-medium">Pranzo:</span> {restaurantInfo.hours.lunch}</p>
                        <p><span className="font-medium">Aperitivo:</span> {restaurantInfo.hours.aperitivo}</p>
                        <p><span className="font-medium">Cena:</span> {restaurantInfo.hours.dinner}</p>
                        <p className="text-red-600 font-medium mt-2">{restaurantInfo.hours.closed}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="p-8 border-slate-200 shadow-xl">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Inviaci un Messaggio
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-slate-700">Nome</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-2"
                  placeholder="Il tuo nome"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-2"
                  placeholder="tua.email@esempio.com"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-slate-700">Telefono</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-2"
                  placeholder="+39 123 456 7890"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-700">Messaggio</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="mt-2"
                  rows={5}
                  placeholder="Scrivi qui il tuo messaggio..."
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold py-3 hover:scale-105 transition-all duration-300"
              >
                Invia Messaggio
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};
